<script setup>
// Platforma ozel secenek bolumu.
//
// Buraya kadar (metin, medya, hesap secimi) TUM platformlar ayni arayuzu
// kullanir; asil ayrim burada baslar. Bilesen HICBIR platformu kendi icinde
// bilmiyor: ne cizecegini icerik-platformlar.js'teki yetenek tablosundan
// okuyor. Yeni bir platform eklendiginde bu dosya DEGISMEZ.
//
// KAPALI ("yakinda") secenekleri gizlemek yerine SEBEBIYLE gosteriyoruz:
// kullanicinin bekledigi bir ozelligin yoklugu, ekranda hic anilmamasindan
// daha iyi anlasilir. Tiklandiginda hicbir sey yapmayan bir kutucuk ise en
// kotusu - olmayan ozelligi varmis gibi gostermek demek.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  DURUM_ACIK,
  DURUM_GIZLI,
  platformKurali,
} from './icerik-platformlar';

const props = defineProps({
  // Sunucudan gelen platform adi: 'instagram' | 'facebook' | ...
  platform: { type: String, required: true },
  // Gonderinin secenekler[platform] kutusu.
  modelValue: { type: Object, default: () => ({}) },
  // Bu platformdan SECILI olan hesaplar (ad, gorsel_url, yayinlanabilir).
  hesaplar: { type: Array, default: () => [] },
  // Gonderiye eklenmis medya; secenek notlarini medyaya gore yaziyoruz.
  medya: { type: Array, default: () => [] },
  // Gonderi yayinlandiysa ayarlar SALT OKUNUR. Degistirilebilir birakmak,
  // Meta'da duran gonderiyi degistirdigi izlenimi verirdi.
  kilitli: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const kural = computed(() => platformKurali(props.platform));

// Gizli secenekler hic cizilmez.
const secenekler = computed(
  () => kural.value?.secenekler.filter(s => s.durum !== DURUM_GIZLI) || []
);

const deger = ad => props.modelValue?.[ad];

// Tek bir alani degistirir. Nesneyi KOPYALAYIP yolluyoruz: ust bilesen
// degisikligi ancak yeni bir nesne gorurse fark eder ve "kaydedilmemis
// degisiklik" uyarisi dogru calisir.
const yaz = (ad, yeniDeger) => {
  emit('update:modelValue', { ...props.modelValue, [ad]: yeniDeger });
};

const secimUyarisi = secenek => {
  const secili = secenek.degerler?.find(d => d.deger === deger(secenek.ad));
  return secili?.uyari || '';
};

// Bazi notlar her zaman gecerli degil: Facebook baglantisi YALNIZCA medyasiz
// gonderide isleniyor. Kosul saglandiginda notu vurguluyoruz, cunku o an
// kullanicinin girdigi deger sessizce yok sayilacak demektir. Kosulu
// secenegin kendisi bildiriyor; burada platform adi gecmez.
const notVurgulu = secenek =>
  secenek.notVurgusu === 'medya_varsa' && props.medya.length > 0;

const acikMi = secenek => secenek.durum === DURUM_ACIK;
</script>

<template>
  <section
    v-if="kural"
    class="flex flex-col gap-4 p-4 border rounded-lg border-n-weak bg-n-surface-1"
  >
    <header class="flex items-center gap-2">
      <span class="size-4" :class="[kural.simge, kural.renk]" />
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('ICERIK.OLUSTUR.SECENEK_BASLIK', { platform: t(kural.anahtar) }) }}
      </h3>
    </header>

    <div
      v-for="secenek in secenekler"
      :key="secenek.ad"
      class="flex flex-col gap-1.5"
    >
      <!-- SECIM: radyo dugmeleri. Az sayida, birbirini dislayan secenek
           (Gonderi / Reel / Story) acilir listede saklanacak bir sey degil;
           kullanici hepsini ayni anda gormeli. -->
      <template v-if="secenek.tur === 'secim'">
        <span class="text-sm text-n-slate-11">{{ t(secenek.anahtar) }}</span>
        <div class="flex flex-wrap gap-4">
          <label
            v-for="oge in secenek.degerler"
            :key="oge.deger"
            class="flex items-center gap-2 text-sm"
            :class="
              oge.durum === DURUM_ACIK
                ? 'text-n-slate-12 cursor-pointer'
                : 'text-n-slate-10 cursor-not-allowed'
            "
            :title="oge.sebep ? t(oge.sebep) : ''"
          >
            <input
              type="radio"
              class="!mb-0"
              :name="`${platform}-${secenek.ad}`"
              :value="oge.deger"
              :checked="deger(secenek.ad) === oge.deger"
              :disabled="oge.durum !== DURUM_ACIK || kilitli"
              @change="yaz(secenek.ad, oge.deger)"
            />
            {{ t(oge.anahtar) }}
            <span
              v-if="oge.durum !== DURUM_ACIK"
              class="px-1.5 py-0.5 text-xs rounded bg-n-alpha-1 text-n-slate-11"
            >
              {{ t('ICERIK.OLUSTUR.YAKINDA') }}
            </span>
          </label>
        </div>
        <p
          v-if="secimUyarisi(secenek)"
          class="flex items-start gap-2 p-2 text-xs rounded-lg bg-n-alpha-1 text-n-slate-11"
        >
          <span class="mt-px shrink-0 i-lucide-info size-3.5" />
          <span>{{ t(secimUyarisi(secenek)) }}</span>
        </p>
      </template>

      <!-- LISTE: acilir secici. Kapali degerler listede DURUR ama secilemez;
           kullanici ozelligin var oldugunu ama henuz acilmadigini gorsun. -->
      <template v-else-if="secenek.tur === 'liste'">
        <label class="text-sm text-n-slate-11">
          {{ t(secenek.anahtar) }}
          <select
            :value="deger(secenek.ad)"
            :disabled="kilitli"
            @change="yaz(secenek.ad, $event.target.value)"
          >
            <option
              v-for="oge in secenek.degerler"
              :key="oge.deger"
              :value="oge.deger"
              :disabled="oge.durum !== DURUM_ACIK"
            >
              {{ t(oge.anahtar) }}
              <template v-if="oge.durum !== DURUM_ACIK">
                ({{ t('ICERIK.OLUSTUR.YAKINDA') }})
              </template>
            </option>
          </select>
        </label>
      </template>

      <!-- METIN: tek ya da cok satirli serbest alan. -->
      <template v-else-if="secenek.tur === 'metin'">
        <label class="text-sm text-n-slate-11">
          {{ t(secenek.anahtar) }}
          <textarea
            v-if="secenek.cokSatirli"
            rows="2"
            :value="deger(secenek.ad) || ''"
            :maxlength="secenek.sinir"
            :placeholder="secenek.yer ? t(secenek.yer) : ''"
            :disabled="!acikMi(secenek) || kilitli"
            @input="yaz(secenek.ad, $event.target.value)"
          />
          <input
            v-else
            type="url"
            :value="deger(secenek.ad) || ''"
            :placeholder="secenek.yer ? t(secenek.yer) : ''"
            :disabled="!acikMi(secenek) || kilitli"
            @input="yaz(secenek.ad, $event.target.value)"
          />
        </label>
        <p
          v-if="secenek.not"
          class="text-xs"
          :class="notVurgulu(secenek) ? 'text-n-amber-11' : 'text-n-slate-11'"
        >
          {{ t(secenek.not) }}
        </p>
      </template>

      <!-- HESAP LISTESI: secim yukaridaki kutucuklarda yapiliyor; burada
           yalnizca gosteriyoruz. Ikinci bir secici, ayni secimin iki ayri
           yerde tutulmasi demekti. -->
      <template v-else-if="secenek.tur === 'hesap_listesi'">
        <span class="text-sm text-n-slate-11">{{ t(secenek.anahtar) }}</span>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="hesap in hesaplar"
            :key="hesap.inbox_id"
            class="px-2 py-1 text-xs rounded-md bg-n-alpha-1 text-n-slate-12"
          >
            {{ hesap.ad }}
          </span>
        </div>
        <p v-if="secenek.not" class="text-xs text-n-slate-11">
          {{ t(secenek.not) }}
        </p>
      </template>

      <!-- DUGME: henuz yapamadigimiz ozellik. Kapali ve sebebi yazili. -->
      <template v-else-if="secenek.tur === 'dugme'">
        <div class="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled
            class="px-2 py-1 text-xs border rounded-md cursor-not-allowed border-n-weak text-n-slate-10"
          >
            {{ t(secenek.anahtar) }}
          </button>
          <span
            class="px-1.5 py-0.5 text-xs rounded bg-n-alpha-1 text-n-slate-11"
          >
            {{ t('ICERIK.OLUSTUR.YAKINDA') }}
          </span>
          <span v-if="secenek.sebep" class="text-xs text-n-slate-11">
            {{ t(secenek.sebep) }}
          </span>
        </div>
      </template>
    </div>
  </section>
</template>
